/* eslint-disable no-param-reassign */
/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { getDefaultPagedContent, Result } from '@src/common/types';
import { ajax } from '@src/common/ajax';
import { ApiUrl } from '@src/common/ajax/apiUrl';
import { getResultStatus } from '@src/common/util/getResultStatus';
import { getUid } from '@src/common/util/getUid';
import {
  RaFilterPanelState,
  RiskScoreData,
  RiskScoreDataApiResp,
  RiskScoreSearchApiResponse,
  RiskScoreSearchResponse,
  RiskSignal,
  RiskSignalApiResp,
} from '../types';
import { toRiskScoreSearchRequest } from './toRiskScoreSearchRequest';
import { getDefaultRiskScoreSearchResponse } from './getDefaultRiskScoreSearchResponse';

export function sortSignal(a: RiskSignal, b: RiskSignal): number {
  if (b.weight !== a.weight) {
    return b.weight - a.weight;
  }
  return a.name.localeCompare(b.name);
}

export async function fetchRiskScores(
  fpState: RaFilterPanelState,
  pageIndex: number
): Promise<Result<RiskScoreSearchResponse>> {
  const request = toRiskScoreSearchRequest({
    now: new Date(),
    fpState,
    pageIndex,
  });
  const body = JSON.stringify(request);
  const riskScoresResp = await ajax<RiskScoreSearchApiResponse>(
    getDefaultRiskScoreSearchResponse(),
    ApiUrl.riskScores,
    { method: 'post', body }
  );

  return {
    resultStatus: getResultStatus(
      riskScoresResp.success,
      riskScoresResp.payload.content
    ),
    result: fromApiResponse(sanitize(riskScoresResp.payload)),
  };

  function fromApiResponse(
    resp: RiskScoreSearchApiResponse
  ): RiskScoreSearchResponse {
    return {
      ...resp,
      content: resp.content.map(toRiskScoreData),
    };

    function toRiskScoreData(item: RiskScoreDataApiResp): RiskScoreData {
      return {
        ...item,
        id: getUid(),
        riskSignals: item.riskSignals.map(toRiskSignal).sort(sortSignal),
      };
    }

    function toRiskSignal(item: RiskSignalApiResp): RiskSignal {
      return {
        ...item,
        id: getUid(),
      };
    }
  }

  function sanitize(
    resp: RiskScoreSearchApiResponse
  ): RiskScoreSearchApiResponse {
    let sanitized: RiskScoreSearchApiResponse = {
      content: [],
      ...getDefaultPagedContent(),
    };
    sanitized = { ...sanitized, ...resp };
    sanitized.content = sanitized.content ?? [];
    sanitized.content = sanitized.content.filter((elem) => !!elem);
    sanitized.content = sanitized.content.map(sanitizeElement);
    return sanitized;
  }

  function sanitizeElement(item: RiskScoreData): RiskScoreData {
    return {
      id: item.id ?? getUid(),
      fraudPatternTypeCode: item.fraudPatternTypeCode ?? '',
      matchedAccountType: item.matchedAccountType ?? '',
      matchedAccountValue: item.matchedAccountValue ?? 0,
      riskScore: item.riskScore ?? 0,
      lastActivityDateTime: item.lastActivityDateTime ?? '',
      riskThresholdId: item.riskThresholdId ?? 0,
      riskThresholdName: item.riskThresholdName ?? '',
      riskThresholdDescription: item.riskThresholdDescription ?? '',
      riskScoreTrend: item.riskScoreTrend ?? '',
      riskSignals: (item.riskSignals ?? []).map(sanitizeRiskSignal),
    };
  }

  function sanitizeRiskSignal(item: RiskSignal): RiskSignal {
    return {
      id: item.id ?? getUid(),
      name: item.name ?? '',
      weight: item.weight ?? 0,
      weightAssignedDateTime: item.weightAssignedDateTime ?? '',
      expiryDateTime: item.expiryDateTime ?? '',
      eventIds: item.eventIds ?? [],
    };
  }
}
